// Resource for learning react-dropzone:
//
//  https://blog.openreplay.com/create-a-drag-and-drop-zone-in-react-with-react-dropzone
//
//  https://www.npmjs.com/package/react-dropzone
//

import { FC, useContext, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Collapse, Grow, Slide, styled, Typography, useTheme, Zoom } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AlertContext from '@/context/AlertContext';


interface IDropzoneProps {
    primaryText?: string;
    secondaryText?: string;
    validFileTypes?: { [key: string]: string[] }
    setFile: (file: File) => void
}

const Container = styled(Box)(({ theme }) => ({
    border: `3px dashed ${theme.palette.primary.main}55`,
    borderRadius: '15px',
    padding: '100px 0',
    '&:hover': {
        cursor: 'pointer'
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    transition: 'all 0.5s',
    height: '100%'
}));

const DropzoneLabel = styled(Box)(({ theme }) => ({
    color: theme.palette.primary.main
}));

const StyledFileDownloadIcon = styled(FileDownloadIcon)(({ theme }) => ({
    display: 'block',
    fontSize: '7rem',
    color: theme.palette.primary.dark,
    transition: 'all 0.25s'
}))

// Valid file types allowed for redaction
const defaultValidFileTypes = {
    'application/pdf': ['.pdf'],        // PDFs 
    // 'application/msword': ['.doc'],      // Word Docs
    // 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] 
}

const Dropzone: FC<IDropzoneProps> = ({
    primaryText,
    secondaryText,
    validFileTypes,
    setFile
}) => {

    const theme = useTheme();

    const { addAlert } = useContext(AlertContext);

    const [dragging, setDragging] = useState<boolean>(false);

    const { getRootProps, getInputProps, acceptedFiles, fileRejections } = useDropzone({
        accept: validFileTypes ?? defaultValidFileTypes
    });

    useEffect(() => {
        if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
        } 
        else if (fileRejections.length > 0) {
            setDragging(false);
            addAlert({
                content: 'Invalid file type',
                timeout: 8000,
                severity: "error",
                dismissable: true
            });
        }
    }, [acceptedFiles, fileRejections])

    return (
        <Container {...getRootProps({ className: "dropzone" })}
            onDragEnter={() => setDragging(true)}
            onDragLeave={() => setDragging(false)}
            sx={{ 
                backgroundColor: dragging ? 
                    `${theme.palette.primary.main}11` : 
                    'none'
            }}
        >
            <input className="input-zone" {...getInputProps()} accept="images/*" />
            <StyledFileDownloadIcon 
                sx={{ 
                    paddingTop: !dragging ? '0' : '30px'
                }} 
            />
            <DropzoneLabel justifyContent={'center'}>
                <Grow in={!dragging}>
                    <Box>
                        <Typography 
                            fontWeight='500' 
                            variant="h2"
                        >
                            {primaryText ?? "Drag and Drop Files Here"}
                        </Typography>
                        <Typography 
                            fontWeight='400' 
                            align='center' 
                            color={theme.palette.secondary.main} 
                            variant="h5"
                        >
                            {secondaryText ?? null}
                        </Typography>
                    </Box>
                </Grow>
            </DropzoneLabel>
        </Container>
    )
}

export default Dropzone